import {
  faBuilding,
  faGear,
  faPowerOff,
  faUser
} from '@fortawesome/pro-regular-svg-icons'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import { type ReactElement, useRef, useState } from 'react'
import { usePathViewedActivity } from '~/api/queries/pathViewed'
import { Dropdown as SelectDropdown } from '~/components/Form/Input/Dropdown'
import { NotificationIndicator } from '~/components/Global/Elements/NotificationIndicator/NotificationIndicator'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { useActiveAgency } from '~/domains/users/hooks/useActiveAgency'
import { useUserAgencies } from '~/domains/users/queries/useUserAgencies'
import { logoPicture40 } from '~/styles/picture.css'
import { buildV3Url } from '~/utils/legacy'
import { container } from '../../Global/Interactions/Dropdown/Downdown.css'
import { Dropdown } from '../../Global/Interactions/Dropdown/Dropdown'
import {
  agencyName,
  functionName,
  item,
  name,
  notificationIndicatorHeader,
  trigger
} from './Menu.css'

export interface MenuProps {
  legacyUrl?: string | null
  isLegacy?: boolean
  user?: {
    id: number
    name: string
    function: string | null
    picture?: string | null
    notificationCount: number
  }
}

export const Menu = ({ user, legacyUrl }: MenuProps): ReactElement => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const { hasNewActivity } = usePathViewedActivity('/settings', {
    defaultState: true
  })
  const { activeAgency, setActiveAgency } = useActiveAgency()
  const { agencies } = useUserAgencies({ enabled: isOpen })

  const onLogout = () => {
    localStorage.removeItem('id')
    localStorage.removeItem('token')
    localStorage.removeItem('instance')
    localStorage.removeItem('email')
    localStorage.removeItem('mainToken')
    localStorage.removeItem('mainUser')
    localStorage.removeItem('mainId')
    localStorage.removeItem('currentGroup')
    window.location.href = `${legacyUrl ?? ''}/users/logout`
  }

  if (user === undefined) {
    return <Shimmer width={40} height={40} />
  }

  return (
    <div className={container} ref={menuRef}>
      <span onClick={() => setOpen(!isOpen)} className={trigger}>
        <span className={name}>
          {user.name}
          {activeAgency !== null ? (
            <span className={agencyName}>
              <FontAwesomeIcon icon={faBuilding} />
              {activeAgency.name}
            </span>
          ) : user.function !== null && user.function !== '' ? (
            <span className={functionName}>
              <FontAwesomeIcon icon={faUser} />
              {user.function}
            </span>
          ) : null}
        </span>
        {user.picture !== undefined && (
          <img
            src={user.picture === null ? 'default.png' : user.picture}
            alt=""
            className={logoPicture40}
          />
        )}
        {user.picture === undefined && <Shimmer width={40} height={40} />}
        <FontAwesomeIcon icon={faChevronDown} />
        {hasNewActivity && (
          <NotificationIndicator className={notificationIndicatorHeader} />
        )}
      </span>
      <Dropdown
        isOpen={isOpen}
        onclose={() => setOpen(false)}
        dropdownRef={menuRef}
        offsetValue={25}
      >
        {agencies.length > 1 && activeAgency !== null && (
          <SelectDropdown
            key={activeAgency.id}
            small
            fullWidth
            searchable
            required
            defaultValue={String(activeAgency.id)}
            options={agencies.map((a) => ({
              label: a.name,
              value: String(a.id)
            }))}
            onChange={(value) => {
              const selected = agencies.find((a) => a.id === Number(value))
              if (selected) {
                setActiveAgency(selected)
                setOpen(false)
              }
            }}
          />
        )}
        {window.JobConfig !== undefined && (
          <a
            href={buildV3Url('/settings')}
            className={item}
            style={{ position: 'relative' }}
          >
            <FontAwesomeIcon icon={faGear} />
            Préférences
            {hasNewActivity && <NotificationIndicator />}
          </a>
        )}
        {window.JobConfig === undefined && (
          <Link
            to="/settings"
            className={item}
            style={{ position: 'relative' }}
            onClick={() => {
              setOpen(false)
            }}
          >
            <FontAwesomeIcon icon={faGear} />
            Préférences
            {hasNewActivity && <NotificationIndicator />}
          </Link>
        )}
        <button onClick={onLogout} type="button" className={item}>
          <FontAwesomeIcon icon={faPowerOff} />
          Déconnexion
        </button>
      </Dropdown>
    </div>
  )
}
